import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Slide,
  Typography,
  Box,
  Avatar,
  IconButton,
  GlobalStyles,
  alpha,
  useTheme,
} from '@mui/material';
import {
  ChatBubbleRounded,
  Close,
  Groups,
  LoginRounded,
  LogoutRounded,
  NotificationsActiveOutlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useUserContext } from '../../context/user';
import { useAuthContext } from '../../context/auth';
import UserAvatar from '../chat/UserAvatar';
import socket from '../../utils/socket';
import { getActiveChatUserId, requestOpenChat } from '../../utils/chatState';
import { avatarColor, avatarSrc, getInitials } from '../../utils/avatar';

const MAX_TOASTS = 5;

const CHAT_DURATION = 5000;
const SYSTEM_DURATION = 6000;
const TOAST_WIDTH = 360;         // one width for every toast: a tidy column; long text wraps (system) or ends with "…" (chat)
const TOAST_Z_INDEX = 2100;      // above dialogs (1300), tooltips (1500) and most overlays

// Unique React key for every toast (Date.now() can repeat inside the same millisecond)
let toastUid = 0;

// ---------- Sounds ----------
// Put these files in /public/sounds/
//   message.mp3     -> new chat message
//   login.mp3       -> user connected / logged in   (system notification, type 0)
//   disconnect.mp3  -> user disconnected            (system notification, type != 0)
const SOUND_FILES = {
  message: '/sounds/message.mp3',
  login: '/sounds/login.mp3',
  disconnect: '/sounds/disconnect.mp3',
};

const sounds = {};

const getSound = (name) => {
  if (!sounds[name]) {
    const audio = new Audio(SOUND_FILES[name]);
    audio.preload = 'auto';
    audio.volume = 0.6;
    sounds[name] = audio;
  }
  return sounds[name];
};

const playSound = (name) => {
  try {
    const audio = getSound(name);
    audio.currentTime = 0;
    // Browsers block audio until the user has interacted with the page once
    audio.play().catch(() => {});
  } catch {
    /* ignore */
  }
};

// Unlock audio on the first click / key press so later sounds are allowed to play
let audioUnlocked = false;
const unlockAudio = () => {
  if (audioUnlocked) return;
  audioUnlocked = true;

  Object.keys(SOUND_FILES).forEach((name) => {
    const audio = getSound(name);
    audio.muted = true;
    audio
      .play()
      .then(() => {
        audio.pause();
        audio.currentTime = 0;
        audio.muted = false;
      })
      .catch(() => {
        audio.muted = false;
      });
  });
};

const NotificationAvatar = ({ n, users }) => {
  const theme = useTheme();

  // Chat: the sender. System (connected / disconnected): the user the message is about
  const userId = n.kind === 'chat' ? n.senderId : n.userId;
  const name = n.kind === 'chat' ? n.senderName : n.name;

  const circle = {
    width: 40,
    height: 40,
    ...avatarColor(name, theme.palette.mode)
  };

  if (n.isGroup) {
    return (
      <Avatar sx={circle}>
        <Groups />
      </Avatar>
    );
  }

  const photo = n.kind === 'chat' ? n.senderAvatar : n.avatar
  if (photo) {
    return (
      <Avatar src={avatarSrc(photo)} sx={circle}>
        {getInitials(name)}
      </Avatar>
    );
  }

  const user = Array.isArray(users)
    ? users.find((u) => String(u._id) === String(userId))
    : null;

  if (user) return <UserAvatar selectedUser={user} />;

  return <Avatar sx={circle}>{getInitials(name)}</Avatar>;
};

// What a toast is about: colour, small badge icon on the avatar, and the label above the title
const toastTone = (n, theme) => {
  if (n.kind === 'chat') {
    return {
      color: theme.palette.secondary.main,
      Icon: n.isGroup ? Groups : ChatBubbleRounded,
      label: n.isGroup ? 'Group message' : 'New message',
    };
  }
  const connected = n.type === 0 || n.type === undefined;
  return connected
    ? { color: theme.palette.success.main, Icon: LoginRounded, label: 'Connected' }
    : { color: theme.palette.error.main, Icon: LogoutRounded, label: 'Disconnected' };
};

/*
 * One toast.
 *
 * It owns its own timer, so the parent no longer re-renders every 50ms
 * (those constant re-renders could swallow clicks on the close button).
 * The progress bar is a CSS animation. Hovering the toast pauses both
 * the timer and the bar, so it cannot vanish while you are about to click it.
 */
const Toast = ({ n, users, onClose, onOpenChat }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [paused, setPaused] = useState(false);

  const duration = n.duration || (n.kind === 'chat' ? CHAT_DURATION : SYSTEM_DURATION);
  const remainingRef = useRef(duration);
  const startedAtRef = useRef(0);

  useEffect(() => {
    if (paused) return;

    startedAtRef.current = Date.now();
    const timer = setTimeout(() => onClose(n.uid), remainingRef.current);

    return () => {
      clearTimeout(timer);
      remainingRef.current = Math.max(0, remainingRef.current - (Date.now() - startedAtRef.current));
    };
  }, [paused, n.uid, onClose]);

  const hasAvatar = n.kind === 'chat' || Boolean(n.userId);
  const isChat = n.kind === 'chat';
  const tone = toastTone(n, theme);
  const ToneIcon = tone.Icon;
  const time = new Date(n.createdAt || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const open = () => isChat && onOpenChat(n);

  return (
    <Slide in appear direction="left" timeout={{ enter: 260 }}>
      <Box
        role={isChat ? 'button' : 'status'}
        tabIndex={isChat ? 0 : undefined}
        aria-live="polite"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onClick={open}
        onKeyDown={(e) => { if (isChat && e.key === 'Enter') open(); }}
        sx={{
          pointerEvents: 'auto',
          position: 'relative',
          overflow: 'hidden',
          width: { xs: 'auto', sm: TOAST_WIDTH },
          maxWidth: { sm: 'calc(100vw - 48px)' },
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
          pl: 2,
          pr: 1,
          pt: 1.5,
          pb: 1.75,
          borderRadius: 2.5,
          cursor: isChat ? 'pointer' : 'default',
          color: theme.palette.text.primary,
          backgroundColor: alpha(theme.palette.background.alt, 0.97),
          backdropFilter: 'blur(8px)',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : theme.palette.neutral[200]}`,
          boxShadow: isDark
            ? '0 12px 32px rgba(0,0,0,0.5), 0 2px 6px rgba(0,0,0,0.3)'
            : '0 12px 28px rgba(0,0,0,0.16), 0 2px 6px rgba(0,0,0,0.08)',
          transition: 'border-color .15s ease, transform .15s ease',
          // accent bar on the left, in the toast's colour
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 4,
            backgroundColor: tone.color,
          },
          ...(isChat && {
            '&:hover, &:focus-visible': {
              outline: 'none',
              borderColor: alpha(tone.color, 0.5),
              transform: 'translateX(-2px)',
            },
          }),
        }}
      >
        {/* Avatar with a small badge for the kind; a plain icon tile when there is nobody to show */}
        <Box sx={{ position: 'relative', flexShrink: 0, mt: 0.25 }}>
          {hasAvatar ? (
            <NotificationAvatar n={n} users={users} />
          ) : (
            <Box
              sx={{
                width: 40,
                height: 40,
                display: 'grid',
                placeItems: 'center',
                borderRadius: '12px',
                color: tone.color,
                backgroundColor: alpha(tone.color, 0.12),
                border: `1px solid ${alpha(tone.color, 0.3)}`,
              }}
            >
              <NotificationsActiveOutlined fontSize="small" />
            </Box>
          )}
          {hasAvatar && (
            <Box
              sx={{
                position: 'absolute',
                right: -4,
                bottom: -4,
                width: 20,
                height: 20,
                display: 'grid',
                placeItems: 'center',
                borderRadius: '50%',
                color: tone.color === theme.palette.secondary.main
                  ? theme.palette.getContrastText(tone.color)
                  : '#fff',
                backgroundColor: tone.color,
                border: `2px solid ${theme.palette.background.alt}`,
              }}
            >
              <ToneIcon sx={{ fontSize: 11 }} />
            </Box>
          )}
        </Box>

        {/* Text */}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
            <Typography
              component="span"
              sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: tone.color }}
            >
              {hasAvatar ? tone.label : 'Notification'}
            </Typography>
            <Typography component="span" sx={{ fontSize: '0.7rem', color: theme.palette.text.secondary }}>
              · {time}
            </Typography>
          </Box>

          <Typography noWrap={isChat} sx={{ fontWeight: 700, fontSize: '0.9rem', lineHeight: 1.3 }}>
            {n.title || (isChat ? 'New message' : 'Notification')}
          </Typography>

          {isChat ? (
            <Typography noWrap sx={{ mt: 0.25, fontSize: '0.83rem', color: theme.palette.text.secondary }}>
              {n.text}
            </Typography>
          ) : (
            <Box
              component="div"
              sx={{
                mt: 0.25,
                fontSize: '0.83rem',
                lineHeight: 1.45,
                color: theme.palette.text.secondary,
                overflowWrap: 'break-word',
                // the server sends style="color:green" / style="color:red": make them readable
                '& [style*="green"]': {
                  color: `${isDark ? theme.palette.success.light : theme.palette.success.dark} !important`,
                  fontWeight: 600,
                },
                '& [style*="red"]': {
                  color: `${isDark ? theme.palette.error.light : theme.palette.error.dark} !important`,
                  fontWeight: 600,
                },
              }}
              dangerouslySetInnerHTML={{ __html: n.body }}
            />
          )}

          {isChat && (
            <Typography sx={{ mt: 0.5, fontSize: '0.7rem', fontWeight: 600, color: alpha(tone.color, 0.85) }}>
              Click to open
            </Typography>
          )}
        </Box>

        {/* Close */}
        <IconButton
          size="small"
          aria-label="Dismiss notification"
          onClick={(e) => {
            // Without this the click bubbles to the toast and opens the chat
            e.stopPropagation();
            onClose(n.uid);
          }}
          sx={{
            flexShrink: 0,
            mt: -0.25,
            p: 0.5,
            color: theme.palette.text.secondary,
            '&:hover': { color: theme.palette.text.primary, backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' },
          }}
        >
          <Close sx={{ fontSize: 16 }} />
        </IconButton>

        {/* Progress bar (CSS animation, paused while hovering) */}
        <Box
          sx={{
            position: 'absolute',
            left: 4,
            right: 0,
            bottom: 0,
            height: 3,
            backgroundColor: alpha(tone.color, 0.12),
            pointerEvents: 'none',
          }}
        >
          <Box
            sx={{
              height: '100%',
              width: '100%',
              backgroundColor: tone.color,
              opacity: 0.8,
              transformOrigin: 'left center',
              animation: `toastShrink ${duration}ms linear forwards`,
              animationPlayState: paused ? 'paused' : 'running',
            }}
          />
        </Box>
      </Box>
    </Slide>
  );
};

const NotificationSnackbar = () => {
  const { users } = useUserContext();
  const { auth } = useAuthContext();

  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);

  // The socket handlers are registered once, so keep my id in a ref (never stale)
  const authIdRef = useRef(auth?._id);
  authIdRef.current = auth?._id;

  const showSystemNotification = (title, body) => {
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      new Notification(title, {
        body,
        icon: '/assets/simbalinkicon.png',
      });
    }
  };

  const stripHtml = (html) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  // Allow sounds after the first user interaction
  useEffect(() => {
    const events = ['click', 'keydown', 'touchstart'];
    const handler = () => {
      unlockAudio();
      events.forEach((e) => window.removeEventListener(e, handler));
    };

    events.forEach((e) => window.addEventListener(e, handler));
    return () => events.forEach((e) => window.removeEventListener(e, handler));
  }, []);

  useEffect(() => {
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    const handleShowNotification = (data) => {
      // type 0 = connected / logged in, anything else = disconnected
      playSound(data.type === 0 || data.type === undefined ? 'login' : 'disconnect');

      setNotifications((prev) =>
        [
          ...prev,
          {
            id: `n-${Date.now()}`,
            uid: ++toastUid,
            createdAt: Date.now(),
            kind: 'system',
            ...data,
          },
        ].slice(-MAX_TOASTS)
      );

      showSystemNotification(
        data.title || 'Notification',
        stripHtml(data.body)
      );
    };

    const handleNewMessage = (msg) => {
      // Muted chat: the message is still delivered to the Chat page, but no popup and no sound
      if (msg.muted) return;

      const tabHidden = document.visibilityState !== 'visible';

      // Group messages belong to the group, 1:1 messages belong to the sender
      const convId = String(msg.groupId || msg.senderId);

      const viewingThisChat = String(getActiveChatUserId()) === convId;

      const isMine = String(msg.senderId) === String(authIdRef.current);

      const preview = msg.content || (msg.attachment?.name ? `📎 ${msg.attachment.name}` : 'Sent you a message');
      const body = msg.forwarded ? `↪ Forwarded: ${preview}` : preview;

      // In a group, show who wrote it
      const text = msg.groupId ? `${msg.senderName}: ${body}` : body;
      const title = msg.groupName || msg.senderName || 'Unknown sender';

      if (tabHidden) {
        showSystemNotification(title, text);
      }

      // Sound: only for messages from others, unless I'm actively looking at that chat
      if (!isMine && !(viewingThisChat && !tabHidden)) {
        playSound('message');
      }

      if (viewingThisChat && !tabHidden) return;

      setNotifications((prev) =>
        [
          ...prev.filter((n) => String(n.id) !== String(msg._id)),
          {
            id: String(msg._id),
            uid: ++toastUid,
            createdAt: Date.now(),
            kind: 'chat',
            type: 0,
            title,
            text,
            convId,
            senderId: String(msg.senderId),
            senderName: msg.senderName,
            senderAvatar: msg.senderAvatar || null,
            isGroup: Boolean(msg.groupId),
          },
        ].slice(-MAX_TOASTS)
      );
    };

    socket.on('showNotification', handleShowNotification);
    socket.on('newMessage', handleNewMessage);

    return () => {
      socket.off('showNotification', handleShowNotification);
      socket.off('newMessage', handleNewMessage);
    };
  }, []);

  const handleClose = useCallback((uid) => {
    setNotifications((prev) => prev.filter((n) => n.uid !== uid));
  }, []);

  const handleOpenChat = useCallback(
    (n) => {
      requestOpenChat(n.convId);
      navigate('/chat');
      handleClose(n.uid);
    },
    [navigate, handleClose]
  );

  if (typeof document === 'undefined') return null;

  // Rendered in a portal on <body>: no parent layout (overflow, transform, z-index)
  // can cover the toasts or block clicks on them, whatever page you are on.
  return createPortal(
    <>
      <GlobalStyles
        styles={{
          '@keyframes toastShrink': {
            from: { transform: 'scaleX(1)' },
            to: { transform: 'scaleX(0)' },
          },
        }}
      />

      <Box
        sx={{
          position: 'fixed',
          top: { xs: 12, sm: 76 },        // below the navbar
          left: { xs: 8, sm: 'auto' },
          right: { xs: 8, sm: 20 },
          zIndex: TOAST_Z_INDEX,
          display: 'flex',
          flexDirection: 'column',
          alignItems: { xs: 'stretch', sm: 'flex-end' },
          gap: 1.25,
          pointerEvents: 'none',        // the empty space around the toasts stays clickable
        }}
      >
        {notifications.map((n) => (
          <Toast
            key={n.uid}
            n={n}
            users={users}
            onClose={handleClose}
            onOpenChat={handleOpenChat}
          />
        ))}
      </Box>
    </>,
    document.body
  );
};

export default NotificationSnackbar;